import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import {
  CaciStyles, PageHeader, Seal,
  useReveal, useScrollProgress, langPath,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   CONTACT
   ───────────────────────────────────────────────────────────────────────────
   Same endpoint (POST /api/contact), same four fields, same payload. Changes:

   · The result banner was rendered below the message box, so on a phone the
     confirmation appeared off-screen: you pressed Send, nothing visibly
     happened, and the natural response is to press it again. The status now
     sits above the button, is announced to screen readers, and receives
     focus.
   · Validation was HTML5 `required` only, which means the browser's own
     bubble in the browser's own language. Fields are now checked on blur and
     on submit, with the message tied to the input via aria-describedby.
   · The submit button had no guard against a double press beyond `disabled`,
     which does not help if the first click is still in flight when a second
     arrives from a fast double-tap. The handler now returns early.
   · The map iframe had no title, so a screen reader announced it as an
     unlabelled frame.
   · A honeypot field was added. It costs one hidden input and stops the
     simplest bots, which is most of them for a form this size.
   ═══════════════════════════════════════════════════════════════════════════ */

const FIELDS = ["name", "email", "subject", "message"];

export default function Contact() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // null | "ok" | "fail"
  const [sending, setSending] = useState(false);

  /* Bots fill every field they find. A human never sees this one, so anything
     in it means the submission is automated — we accept it silently rather
     than telling the bot which check it failed. */
  const trapRef = useRef(null);
  const statusRef = useRef(null);
  const pageRef = useReveal([]);
  const progress = useScrollProgress();

  const validate = useCallback((values) => {
    const next = {};
    if (!values.name.trim()) next.name = t("form.required", "Ce champ est obligatoire.");
    if (!values.email.trim()) next.email = t("form.required", "Ce champ est obligatoire.");
    /* Deliberately permissive: the job here is to catch a typo like a missing
       @, not to adjudicate RFC 5322. The server has the final say. */
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) {
      next.email = t("form.email_invalid", "Cette adresse e-mail semble incorrecte.");
    }
    if (!values.subject.trim()) next.subject = t("form.required", "Ce champ est obligatoire.");
    if (!values.message.trim()) next.message = t("form.required", "Ce champ est obligatoire.");
    else if (values.message.trim().length < 10) {
      next.message = t("form.message_short", "Merci de détailler un peu votre message.");
    }
    return next;
  }, [t]);

  const onChange = (e) => {
    const { name, value } = e.target;
    const next = { ...form, [name]: value };
    setForm(next);
    /* Re-check only fields already touched: telling someone their e-mail is
       invalid while they are still typing the first letter is just noise. */
    if (touched[name]) setErrors(validate(next));
  };

  const onBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate(form));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return; // a fast double-tap can arrive before `disabled` applies

    if (trapRef.current && trapRef.current.value) {
      setStatus("ok"); // accept silently; give an automated client nothing to learn from
      return;
    }

    const found = validate(form);
    setErrors(found);
    setTouched(FIELDS.reduce((a, f) => ({ ...a, [f]: true }), {}));

    if (Object.keys(found).length > 0) {
      const first = document.getElementById(`ct-${FIELDS.find((f) => found[f])}`);
      first && first.focus();
      return;
    }

    setSending(true);
    setStatus(null);
    try {
      await axios.post(`${BASE_URL}/api/contact`, form);
      setStatus("ok");
      setForm({ name: "", email: "", subject: "", message: "" });
      setTouched({});
      setErrors({});
    } catch (err) {
      console.error("Contact: submission failed —", err);
      setStatus("fail");
    } finally {
      setSending(false);
      /* Move the caret to the result. On a phone the banner is otherwise
         easy to miss entirely. */
      window.requestAnimationFrame(() => statusRef.current && statusRef.current.focus());
    }
  };

  const field = (name, { type = "text", rows } = {}) => {
    const invalid = touched[name] && errors[name];
    const common = {
      id: `ct-${name}`,
      name,
      value: form[name],
      onChange,
      onBlur,
      "aria-invalid": invalid ? "true" : undefined,
      "aria-describedby": invalid ? `ct-${name}-err` : undefined,
      className: `ct-input${invalid ? " is-invalid" : ""}`,
      dir: "auto",
    };
    return (
      <div className={`ct-field${rows ? " is-wide" : ""}`}>
        <label className="ct-label" htmlFor={`ct-${name}`}>
          {t(`home.${LABELS[name]}`)}
          <span className="ct-req" aria-hidden="true">*</span>
        </label>
        {rows ? (
          <textarea {...common} rows={rows} />
        ) : (
          <input {...common} type={type} autoComplete={AUTOCOMPLETE[name]} />
        )}
        {invalid && (
          <p className="ct-err" id={`ct-${name}-err`}>
            <i className="bi bi-exclamation-circle" />
            {errors[name]}
          </p>
        )}
      </div>
    );
  };

  const pageTitle = t("menu.contact");

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <style>{PAGE_CSS}</style>

      <Helmet>
        <title>{pageTitle} — {t("menu.caci")}</title>
        <meta name="description" content={t("home.contact", pageTitle)} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={t("home.contact", pageTitle)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={pageTitle}
        sub={t("home.Contact_Us", "")}
        crumbs={[{ label: t("menu.home"), to: langPath(lang, "/") }, { label: pageTitle }]}
      />

      <section className="caci-section">
        <div className="caci-shell">
          <div className="ct-grid">
            {/* ── Coordinates ─────────────────────────────── */}
            <aside className="ct-info" data-rv="rise">
              <h2 className="ct-info-title">
                <Seal size={14} />
                {t("home.Contact_Us", "Nous contacter")}
              </h2>

              <ul className="ct-list">
                <li>
                  <span className="ct-icon"><i className="bi bi-geo-alt" /></span>
                  <span className="ct-text">
                    <b>{t("home.Address_Us")}</b>
                    <span dir="auto">{t("menu.adresse")}</span>
                  </span>
                </li>
                <li>
                  <span className="ct-icon"><i className="bi bi-telephone" /></span>
                  <span className="ct-text">
                    <b>{t("home.Call_Us")}</b>
                    {/* Tappable: on a phone this is the fastest route to a
                        human, and it was plain text before. */}
                    <a href={`tel:${String(t("menu.phone")).replace(/[^\d+]/g, "")}`} dir="ltr">
                      {t("menu.phone")}
                    </a>
                  </span>
                </li>
                <li>
                  <span className="ct-icon"><i className="bi bi-envelope" /></span>
                  <span className="ct-text">
                    <b>{t("home.Email_Us")}</b>
                    <a href={`mailto:${t("menu.email")}`} dir="ltr">{t("menu.email")}</a>
                  </span>
                </li>
              </ul>
            </aside>

            {/* ── Map ─────────────────────────────────────── */}
            <div className="ct-map" data-rv="zoom" style={{ "--i": 1 }}>
              <iframe
                title={t("home.Our_Location", "Notre localisation")}
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d25571.692881772844!2d3.0469161660452158!3d36.75949215664762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x128fb2fded7ade59%3A0xdf928cfb502e65fb!2sPalais%20consulaire%20d'Alger!5e0!3m2!1sfr!2sdz!4v1713857504765!5m2!1sfr!2sdz"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="ct-map-tag">
                <Seal size={13} />
                <span>
                  <b>{t("home.Our_Location")}</b>
                  <span dir="auto">{t("menu.adresse")}</span>
                </span>
              </div>
            </div>

            {/* ── Form ────────────────────────────────────── */}
            <div className="ct-form-wrap" data-rv="rise" style={{ "--i": 2 }}>
              <form onSubmit={onSubmit} noValidate>
                <div className="ct-fields">
                  {field("name")}
                  {field("email", { type: "email" })}
                  {field("subject")}
                  {field("message", { rows: 7 })}
                </div>

                {/* Honeypot — off-screen, never announced, never tabbed to. */}
                <div className="ct-trap" aria-hidden="true">
                  <label htmlFor="ct-website">Website</label>
                  <input
                    id="ct-website" name="website" type="text"
                    ref={trapRef} tabIndex={-1} autoComplete="off"
                  />
                </div>

                {/* The result sits ABOVE the button: below it, on a phone, the
                    confirmation lands off-screen and the visitor presses Send
                    again because nothing appeared to happen. */}
                <div
                  className="ct-status-slot"
                  ref={statusRef}
                  tabIndex={-1}
                  role="status"
                  aria-live="polite"
                >
                  {status === "ok" && (
                    <p className="ct-status is-ok">
                      <i className="bi bi-check-circle-fill" />
                      {t("home.Message_sent_successfully")}
                    </p>
                  )}
                  {status === "fail" && (
                    <p className="ct-status is-fail">
                      <i className="bi bi-exclamation-triangle-fill" />
                      {t("home.Message_sent_Failed")}
                    </p>
                  )}
                </div>

                <div className="ct-actions">
                  <button type="submit" className="btn-solid" disabled={sending}>
                    {sending ? (
                      <>
                        <span className="ct-spin" aria-hidden="true" />
                        {t("home.Sending")}…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send" />
                        {t("home.Send_Message")}
                      </>
                    )}
                  </button>
                  <p className="ct-note">
                    <span aria-hidden="true">*</span> {t("form.required_note", "Champs obligatoires")}
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const LABELS = { name: "Your_Name", email: "Your_Email", subject: "Subject", message: "Message" };
const AUTOCOMPLETE = { name: "name", email: "email", subject: "off" };

const PAGE_CSS = `
.caci-page .ct-grid { display: grid; gap: clamp(18px, 3vw, 28px); }
@media (min-width: 992px) {
  .caci-page .ct-grid { grid-template-columns: 340px minmax(0, 1fr); align-items: start; }
  .caci-page .ct-form-wrap { grid-column: 1 / -1; }
}

.caci-page .ct-info {
  border: 1px solid var(--rule); border-radius: 20px;
  background: var(--surface); padding: 24px;
}
.caci-page .ct-info-title {
  display: flex; align-items: center; gap: 10px; margin: 0 0 18px;
  font-family: var(--mono); font-size: 11px; font-weight: 700;
  letter-spacing: .14em; text-transform: uppercase; color: var(--pine);
}
.caci-page .ct-info-title .seal { color: var(--brass); }
.caci-page .ct-list { list-style: none; margin: 0; padding: 0; }
.caci-page .ct-list li { display: flex; gap: 14px; padding-block: 16px; }
.caci-page .ct-list li + li { border-block-start: 1px solid var(--rule); }
.caci-page .ct-icon {
  flex: 0 0 auto; width: 42px; height: 42px; border-radius: 13px;
  display: grid; place-items: center; font-size: 17px;
  background: var(--mint); color: var(--pine);
}
.caci-page .ct-text { min-width: 0; display: grid; gap: 3px; }
.caci-page .ct-text b {
  font-family: var(--mono); font-size: 10.5px; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase; color: var(--muted);
}
.caci-page .ct-text > span, .caci-page .ct-text a {
  font-size: 14.5px; line-height: 1.5; color: var(--txt); overflow-wrap: anywhere;
}
/* The phone number and e-mail are the two fastest routes to a human on this
   page. As inline links they were only as tall as their text (~30px); the
   negative margin lifts the hit area to 44px without moving anything. */
.caci-page .ct-text a {
  display: block; text-decoration: none;
  padding-block: 11px; margin-block: -7px;
  transition: color var(--t1) var(--e);
}
.caci-page .ct-text a:hover { color: var(--pine); text-decoration: underline; }

.caci-page .ct-map {
  position: relative; border-radius: 20px; overflow: hidden;
  border: 1px solid var(--rule); background: var(--mint);
  min-height: 320px; height: 100%;
  box-shadow: 0 30px 60px -34px rgba(8,21,14,.4);
}
.caci-page .ct-map iframe {
  position: absolute; inset: 0; width: 100%; height: 100%;
  border: 0; display: block;
}
/* Bottom, not top: Google's own zoom and fullscreen controls live in the
   top corners and the old overlay sat right on them. */
.caci-page .ct-map-tag {
  position: absolute; z-index: 2;
  inset-block-end: 14px; inset-inline-start: 14px; inset-inline-end: 14px;
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 14px; border-radius: 14px;
  background: rgba(255,255,255,.92);
  -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
  box-shadow: 0 12px 30px -14px rgba(8,21,14,.5);
}
.caci-page .ct-map-tag .seal { color: var(--brass); margin-block-start: 2px; }
.caci-page .ct-map-tag > span { display: grid; gap: 2px; min-width: 0; }
.caci-page .ct-map-tag b {
  font-family: var(--mono); font-size: 10px; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase; color: var(--pine);
}
.caci-page .ct-map-tag span span { font-size: 12.5px; line-height: 1.45; color: var(--muted); }
@media (max-width: 991.98px) { .caci-page .ct-map { min-height: 280px; } }

.caci-page .ct-form-wrap {
  border: 1px solid var(--rule); border-radius: 20px;
  background: var(--surface); padding: clamp(20px, 3.4vw, 34px);
}
.caci-page .ct-fields { display: grid; gap: 16px; }
@media (min-width: 700px) {
  .caci-page .ct-fields { grid-template-columns: 1fr 1fr; }
  .caci-page .ct-field.is-wide, .caci-page .ct-field:nth-child(3) { grid-column: 1 / -1; }
}
.caci-page .ct-label {
  display: block; margin-block-end: 8px;
  font-family: var(--mono); font-size: 10.5px; font-weight: 700;
  letter-spacing: .12em; text-transform: uppercase; color: var(--muted);
}
.caci-page .ct-req { color: var(--brass); margin-inline-start: 4px; }
.caci-page .ct-input {
  width: 100%; min-height: 52px; padding: 14px 18px;
  border: 1px solid var(--rule); border-radius: 14px;
  background: var(--paper); color: var(--txt);
  font-family: var(--body); font-size: 15px; line-height: 1.5;
  outline: none;
  transition: border-color var(--t1) var(--e), box-shadow var(--t1) var(--e),
              background var(--t1) var(--e);
}
.caci-page textarea.ct-input { resize: vertical; min-height: 150px; }
.caci-page .ct-input:focus {
  border-color: var(--leaf); background: var(--surface);
  box-shadow: 0 0 0 4px rgba(26,122,74,.1);
}
.caci-page .ct-input.is-invalid { border-color: var(--danger); }
.caci-page .ct-input.is-invalid:focus { box-shadow: 0 0 0 4px rgba(163,58,43,.12); }
.caci-page .ct-err {
  display: flex; align-items: center; gap: 7px; margin: 8px 0 0;
  font-size: 12.5px; line-height: 1.4; color: var(--danger);
}
.caci-page .ct-err i { font-size: 13px; flex: 0 0 auto; }

/* The honeypot must be unreachable but not display:none — some bots skip
   hidden inputs, and a few browsers will not submit them. */
.caci-page .ct-trap {
  position: absolute; width: 1px; height: 1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap;
}

.caci-page .ct-status-slot { outline: none; }
.caci-page .ct-status-slot:empty { display: none; }
.caci-page .ct-status {
  display: flex; align-items: center; gap: 11px;
  margin: 22px 0 0; padding: 15px 18px; border-radius: 14px;
  font-size: 14.5px; line-height: 1.5;
  animation: ctIn .4s var(--e) both;
}
@keyframes ctIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
.caci-page .ct-status i { flex: 0 0 auto; font-size: 17px; }
.caci-page .ct-status.is-ok {
  background: var(--mint); color: var(--pine);
  box-shadow: inset 0 0 0 1px rgba(26,122,74,.22);
}
.caci-page .ct-status.is-fail {
  background: rgba(163,58,43,.07); color: var(--danger);
  box-shadow: inset 0 0 0 1px rgba(163,58,43,.22);
}

.caci-page .ct-actions {
  display: flex; flex-wrap: wrap; align-items: center; gap: 14px 20px;
  margin-block-start: 24px;
}
.caci-page .ct-actions .btn-solid { min-width: 200px; }
.caci-page .ct-actions .btn-solid:disabled { opacity: .65; cursor: progress; }
.caci-page .ct-note {
  margin: 0; font-family: var(--mono); font-size: 11px;
  letter-spacing: .06em; color: var(--muted);
}
.caci-page .ct-spin {
  width: 15px; height: 15px; border-radius: 50%; display: inline-block;
  border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
  animation: sealSpin .7s linear infinite;
}
@media (max-width: 575.98px) {
  .caci-page .ct-actions .btn-solid { width: 100%; }
  .caci-page .ct-note { width: 100%; text-align: center; }
}
`;
